// src/validation/validationBuilder.ts
import * as yup from 'yup';

/* Общий alias для yup-схем */
export type AnyYupSchema = yup.Schema<any>;

/* Параметры сборки схемы (generic по ключам полей) */
export interface BuildSchemaOptions<FieldKey extends string = string> {
    overrideRequired?: FieldKey[];
    overrideNotRequired?: FieldKey[];
}

/* Безопасное клонирование схемы (если .clone() доступен) */
const cloneSchema = (schema: AnyYupSchema): AnyYupSchema => {
    // @ts-ignore
    if (typeof schema?.clone === 'function') {
        // @ts-ignore
        return schema.clone();
    }
    return schema;
};

/**
 * Собирает yup.object() схему на основе переданного реестра baseValidators
 * и списка полей (white-list).
 *
 * - baseValidators: Record<FieldKey, AnyYupSchema>
 * - fields: FieldKey[] - какие поля включить в схему
 * - opts: overrideRequired/overrideNotRequired
 */
export const buildSchemaFromBase = <FieldKey extends string>(
    baseValidators: Record<FieldKey, AnyYupSchema>,
    fields: FieldKey[],
    opts: BuildSchemaOptions<FieldKey> = {},
): yup.ObjectSchema<any> => {
    const { overrideRequired = [], overrideNotRequired = [] } = opts;
    const shape: Record<string, AnyYupSchema> = {};

    fields.forEach((key) => {
        const base = baseValidators[key];
        if (!base) return;
        let schema = cloneSchema(base);

        if (overrideNotRequired.includes(key)) {
            schema = schema.notRequired().nullable();
        }

        if (overrideRequired.includes(key)) {
            schema = (schema as AnyYupSchema).required ? (schema as AnyYupSchema).required('Поле обязательно') : schema;
        }

        shape[key] = schema;
    });

    // Спец-кейс: если есть confirm_password без password — делаем confirm не обязательным
    if (fields.includes('confirm_password' as any) && !fields.includes('password' as any)) {
        shape['confirm_password'] = yup
            .string()
            .notRequired()
            .nullable()
            .oneOf([yup.ref('password')], 'Пароли не совпадают');
    }

    return yup.object().shape(shape);
};
